import {
  DateTimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { useState } from "react";
import { Controller } from "react-hook-form";

export function NoteCreatedAtTimer() {
  const [isDateInputValid, setIsDateInputValid] = useState(true);

  const createHandleChange = (fieldOnChange) => (newValue) => {
    var isValidDate = /^\d{2}\/\d{2}\/\d{4}\s$/; // validate that typed date is mm/dd/yyyy
    var now = moment();

    if (newValue?._isValid) {
      setIsDateInputValid(true);
      fieldOnChange(newValue);
    } else {
      if (isValidDate.test(newValue?._i)) {
        let valueWithTime = newValue?._i + "12:00 AM"; // if just date is entered, set it with default time
        if (
          moment(
            valueWithTime,
            "MM/DD/YYYY hh:mm A",
            true,
          ).isValid() &&
          moment(valueWithTime, "MM/DD/YYYY hh:mm A") <= now // moment validates that this is in fact a date and is not in the future
        ) {
          fieldOnChange(moment(valueWithTime, "MM/DD/YYYY hh:mm A"));
          setIsDateInputValid(true);
        } else {
          setIsDateInputValid(false);
        }
      } else {
        setIsDateInputValid(false);
      }
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Controller
        name="createdAt"
        render={({ field }) => (
          <div data-testid="note-created-at-input">
            <DateTimePicker
              label=""
              disableFuture
              value={field.value ? moment(field.value) : null}
              onChange={createHandleChange(field.onChange)}
              ref={field.ref}
              referenceDate={moment()}
              slotProps={{
                openPickerButton: {
                  sx: { color: "var(--color-indigo-500)" },
                },
                textField: {
                  variant: "standard",
                  error: !isDateInputValid,
                  InputProps: {
                    sx: {
                      backgroundColor: "var(--surface-input)",
                      color: "var(--text-primary)",
                      borderRadius: "var(--radius-standard)",
                      fontFamily: "var(--font-family-body)",
                      fontSize: "var(--font-size-sm)",
                      padding: ".25rem .5rem",
                      height: 40,
                      maxWidth: 250,
                      outline: "none",
                      display: "flex",
                      alignItems: "center",
                      border: "2px solid var(--border-input)",
                      boxShadow:
                        "0px 0px 10px 0px var(--shadow-primary)",
                      transition: "all 150ms ease",
                      "& .MuiPickersInputBase-sectionsContainer": {
                        marginTop: "4px",
                      },
                      "&::before": {
                        borderBottom: 0,
                      },
                      "&::after": {
                        border: 0,
                      },
                      "&:hover:not(.Mui-disabled)::before": {
                        borderBottom: 0,
                      },
                      "&:hover:not(.Mui-disabled):not(.Mui-error)": {
                        boxShadow:
                          "0px 0px 10px 0px var(--shadow-focus)",
                      },
                      "&.Mui-focused": {
                        borderColor: "var(--border-brand)",
                        boxShadow:
                          "0px 0px 10px 0px var(--shadow-focus)",
                      },
                      "&.Mui-error": {
                        borderColor: "var(--border-danger)",
                        boxShadow:
                          "0px 0px 10px 0px var(--shadow-focus)",
                      },
                    },
                  },
                },
                desktopPaper: {
                  sx: {
                    backgroundColor: "var(--surface-input)",
                    color: "var(--text-primary)",
                    fontFamily: "var(--font-family-body)",
                    borderRadius: "var(--radius-standard)",
                    boxShadow:
                      "0px 0px 10px 0px var(--shadow-primary)",
                  },
                },
                day: {
                  sx: {
                    fontFamily: "var(--font-family-body)",
                    "&.Mui-selected": {
                      backgroundColor: "var(--color-indigo-500)",
                      "&:hover": {
                        backgroundColor: "var(--color-indigo-600)",
                      },
                    },
                    "&:hover": {
                      backgroundColor:
                        "color-mix(in srgb, var(--color-indigo-500) 50%, transparent)",
                    },
                  },
                },
                actionBar: {
                  sx: {
                    "& .MuiButton-root": {
                      color: "var(--border-brand)",
                      fontFamily: "var(--font-family-body)",
                    },
                  },
                },
                layout: {
                  sx: {
                    "& .MuiPickersCalendarHeader-label": {
                      color: "var(--text-primary)",
                      fontFamily: "var(--font-family-body)",
                    },
                    "& .MuiPickersArrowSwitcher-button": {
                      color: "var(--text-primary)",
                    },
                    "& .MuiDayCalendar-weekDayLabel": {
                      color: "var(--text-secondary)",
                      fontFamily: "var(--font-family-body)",
                    },
                    "& .MuiPickersDay-root": {
                      color: "var(--text-primary)",
                    },
                    "& .MuiPickersDay-today": {
                      borderColor: "var(--border-brand)",
                    },
                    "& .MuiMultiSectionDigitalClock-root": {
                      backgroundColor: "var(--surface-input)",
                    },
                    // Time picker items (hours, minutes, AM/PM)
                    "& .MuiMultiSectionDigitalClockSection-item": {
                      color: "var(--text-primary)",
                      fontFamily: "var(--font-family-body)",
                      "&.Mui-selected": {
                        backgroundColor: "var(--color-indigo-500)",
                        "&:hover": {
                          backgroundColor: "var(--color-indigo-600)",
                        },
                      },
                      "&:hover": {
                        backgroundColor:
                          "color-mix(in srgb, var(--color-indigo-500) 50%, transparent)",
                      },
                    },
                    "& .MuiMenuItem-root": {
                      color: "var(--text-primary)",
                      fontFamily: "var(--font-family-body)",
                      "&.Mui-selected": {
                        backgroundColor: "var(--color-indigo-500)",
                        "&:hover": {
                          backgroundColor: "var(--color-indigo-600)",
                        },
                      },
                      "&:hover": {
                        backgroundColor:
                          "color-mix(in srgb, var(--color-indigo-500) 50%, transparent)",
                      },
                    },
                  },
                },
              }}
            />
          </div>
        )}
      />
    </LocalizationProvider>
  );
}
